import React, { useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import BaseApiService from '../auditor/service/baseApiService.mjs';
import PtaeBucketAssigner from './PtaeBucketAssigner.mjs';
import { usePtaeOptions, ptaeIndexOf, PtaeCodeLabel } from './PtaeCascadePicker.mjs';

/**
 * Claim-level PTAE allocation, assigned by the admin before approval.
 *
 * ONE WAY TO FUND A CLAIM: the Assign PTAE button. It opens the bucket
 * assigner, where each day or receipt goes into a PTAE bucket - one bucket
 * holding every line funds the whole claim from one code. The single
 * "PTA / Code" picker that used to sit on this panel was removed on 2026-10-16
 * at the user's request, funding having moved to buckets; the old "split
 * across PTAs" mode (amounts typed against each code) went before it. Bucket
 * amounts are derived from the lines, so they cannot drift.
 *
 * Claims funded the old ways still exist - one code for the whole claim, or
 * typed amounts. They are shown here read-only, and Assign PTAE replaces them:
 * an old single code opens in the assigner as one bucket holding every line.
 *
 * Saving happens when the assigner is closed. There is no Save button on this
 * panel.
 */
const api = new BaseApiService();

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
const money = (n) => (Number(n) || 0).toLocaleString('en-US', {
  minimumFractionDigits: 2, maximumFractionDigits: 2,
});

/**
 * A split line's parts at a new line value, keeping their proportions - the
 * same rule the server applies when the claim is saved (shareLine in
 * claimLineReconcile.mjs), so what the panel shows mid-edit is what saving
 * will store. Whole cents, remainder on the last part.
 */
const shareLine = (total, previous) => {
  const n = previous.length;
  if (n <= 1) return [round2(total)];
  const totalCents = Math.round((Number(total) || 0) * 100);
  const weights = previous.map(v => Math.max(0, Number(v) || 0));
  const weightSum = weights.reduce((a, b) => a + b, 0);
  const out = [];
  let given = 0;
  for (let i = 0; i < n - 1; i++) {
    const cents = Math.floor(totalCents * (weightSum > 0 ? weights[i] / weightSum : 1 / n));
    out.push(cents);
    given += cents;
  }
  out.push(totalCents - given);
  return out.map(c => c / 100);
};

const ClaimPtaeAllocation = ({
  claimType, claimId, claimTotal, readOnly = false, onChange,
  onAllocationLoaded, liveLineValues = null, refreshKey = null, onSaveClaim = null,
}) => {
  const [ptae, setPtae] = useState('');
  // Codes are shown with their descriptions, as the picker shows them.
  const { options: ptaeOptions } = usePtaeOptions();
  const ptaeIndex = useMemo(() => ptaeIndexOf(ptaeOptions), [ptaeOptions]);
  const [splits, setSplits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // 'idle' | 'saved'. With no Save button to press, the admin needs to see
  // that the assigner's change was taken.
  const [saveState, setSaveState] = useState('idle');
  const [lines, setLines] = useState([]);
  const [lineAssigned, setLineAssigned] = useState(false);
  const [staleAssignments, setStaleAssignments] = useState(0);
  const [showAssigner, setShowAssigner] = useState(false);
  // Saving the claim before the assigner opens (see openAssigner).
  const [savingFirst, setSavingFirst] = useState(false);

  const load = useCallback(async () => {
    if (!claimId || !claimType) return;
    setLoading(true);
    try {
      const res = await api.api.get(`/admin/claims/${claimType}/${claimId}/ptae`);
      const data = res.data?.data || {};
      const loadedSplits = data.splits || [];
      setPtae(data.ptae || '');
      setSplits(loadedSplits);
      setLines(data.lines || []);
      setLineAssigned(!!data.lineAssigned);
      setStaleAssignments(data.staleAssignments || 0);
      setError(null);

      // Hand the allocation up so the page's Excel export can label each line
      // with its PTAE. The export is a pure util with no API client of its own,
      // and this component is the only thing that already fetches this - so it
      // publishes rather than having the page fetch the same endpoint twice.
      if (onAllocationLoaded) {
        const byId = {};
        const partsById = {};
        const codeOf = (splitId) => loadedSplits.find(
          sp => String(sp.id ?? sp._id) === String(splitId)
        )?.ptae || '';
        for (const line of (data.lines || [])) {
          const parts = (line.allocations || [])
            .map(a => ({ code: codeOf(a.splitId), amount: Number(a.amount) || 0 }))
            .filter(a => a.code);
          // A split line names each part, the same text the emailed workbook uses.
          const code = parts.length > 1
            ? parts.map(pt => `${pt.code} ($${money(pt.amount)})`).join(' + ')
            : (parts[0]?.code || codeOf(line.splitId) || data.ptae || '');
          if (code) byId[String(line.lineId)] = code;
          if (parts.length > 1) partsById[String(line.lineId)] = parts;
        }
        onAllocationLoaded({
          linePtae: byId,
          lineParts: partsById,
          splits: loadedSplits,
          ptae: data.ptae || null,
          lineAssigned: !!data.lineAssigned,
        });
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load PTAE allocation');
    } finally {
      setLoading(false);
    }
  }, [claimType, claimId, onAllocationLoaded]);

  useEffect(() => { load(); }, [load]);

  // Reload after the claim itself is saved. The server re-prices the edited
  // lines and resizes the buckets to match, and this panel used to keep showing
  // the figures from when the page opened - so an admin corrected a receipt,
  // saved, and the funding still showed the old amount.
  useEffect(() => {
    if (refreshKey) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  const total = parseFloat(claimTotal) || 0;

  /**
   * Each line as the form currently has it.
   *
   * The lines come from the server, so while an admin edits a receipt or drops
   * a meal they showed the SAVED amount and the buckets did not move. With the
   * form's own values passed in, the line, its bucket and the totals follow the
   * edit as it is typed; saving then stores exactly what is shown here.
   */
  const liveLines = lines.map(line => {
    const key = String(line.lineId);
    const hasLive = liveLineValues && Object.prototype.hasOwnProperty.call(liveLineValues, key);
    const removed = !!liveLineValues && !hasLive;
    const amount = removed ? 0 : round2(hasLive ? liveLineValues[key] : line.amount);
    const allocations = line.allocations || (line.splitId ? [{ splitId: line.splitId, amount: line.amount }] : []);
    const shares = shareLine(amount, allocations.map(a => a.amount));
    return {
      ...line,
      allocations,
      liveAmount: amount,
      changed: Math.abs(amount - round2(line.amount)) > 0.004,
      removed,
      liveParts: allocations.map((a, i) => ({ ...a, liveAmount: shares[i] })),
    };
  });
  const unsavedNewLines = liveLineValues
    ? Object.keys(liveLineValues).filter(id => !lines.some(l => String(l.lineId) === String(id)))
    : [];
  const unsavedNewValue = unsavedNewLines.reduce((t, id) => t + (Number(liveLineValues[id]) || 0), 0);
  const linesEdited = liveLines.some(l => l.changed || l.removed) || unsavedNewLines.length > 0;

  // The assigner works on what is stored. A line removed in the form is still
  // stored until the claim is saved, and showing it there invited funding a
  // line that was about to go.
  const assignableLines = lines.filter(
    l => !liveLines.some(live => live.removed && String(live.lineId) === String(l.lineId))
  );

  // One PTAE funding every line. A line added to the claim joins it when the
  // claim is saved (the server's resyncClaimPtaeAllocation), so the bucket
  // already counts it here. With several PTAEs a new line is left for the
  // admin to place - there is no telling which award it belongs to.
  const soleBucket = lineAssigned && splits.length === 1 ? splits[0] : null;
  const soleBucketId = soleBucket ? String(soleBucket.id ?? soleBucket._id) : null;

  /** Each bucket's total from the live lines - what saving will store. */
  const bucketRows = splits.map(sp => {
    const id = String(sp.id ?? sp._id);
    let amount = 0;
    let count = 0;
    let splitCount = 0;
    for (const l of liveLines) {
      const part = l.liveParts.find(pt => String(pt.splitId) === id);
      if (!part) continue;
      amount += part.liveAmount;
      count += 1;
      if (l.liveParts.length > 1) splitCount += 1;
    }
    if (id === soleBucketId) {
      amount += unsavedNewValue;
      count += unsavedNewLines.length;
    }
    return {
      id,
      ptae: sp.ptae,
      count,
      splitCount,
      amount: round2(amount),
      saved: round2(parseFloat(sp.amount) || 0),
    };
  });
  const bucketsAllocated = round2(bucketRows.reduce((t, b) => t + b.amount, 0));

  /**
   * Commit a line-item allocation.
   *
   * Its own endpoint rather than the amount-based one: the amounts are derived
   * from the lines server-side, so there is nothing for the client to total up
   * and nothing to disagree about.
   */
  const saveLineBuckets = async ({ buckets }) => {
    try {
      const res = await api.api.put(
        `/admin/claims/${claimType}/${claimId}/ptae/lines`,
        { buckets }
      );
      return res.data;
    } catch (err) {
      // Rethrow with the SERVER's message. The assigner shows err.message, and
      // an axios error's own message is only "Request failed with status code
      // 400" - which would have hidden every useful reason the write was
      // refused ("a line item was assigned to more than one PTAE", "one of the
      // line items is no longer on this claim").
      throw new Error(
        err.response?.data?.message || 'Failed to assign PTAEs by line item'
      );
    }
  };

  /**
   * Open the assigner on the claim's lines as they now stand.
   *
   * Lines added or removed in the form are not stored until the claim is
   * saved, and the assigner can only fund stored lines - so a line just added
   * was missing from it and a line just removed was still in it. With unsaved
   * line changes the claim is saved first (the button says so), and the
   * assigner opens on what that save stored.
   */
  const openAssigner = async () => {
    if (linesEdited && onSaveClaim) {
      setSavingFirst(true);
      try {
        // false: the admin cancelled a question the save asked. A failed save
        // throws, and the form shows why.
        if (await onSaveClaim() !== true) return;
        await load();
      } catch (_err) {
        return;
      } finally {
        setSavingFirst(false);
      }
    }
    setShowAssigner(true);
  };

  const handleAssignerSaved = async (result) => {
    setShowAssigner(false);
    setSaveState('saved');
    setTimeout(() => setSaveState(s => (s === 'saved' ? 'idle' : s)), 3000);
    await load();
    if (onChange) onChange(result?.data || {});
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 mb-4 sm:mb-6">
        <p className="text-gray-500 text-sm">Loading PTA allocation…</p>
      </div>
    );
  }

  // Funded by amounts typed against codes, with no line items behind them: a
  // claim allocated before the bucket assigner replaced that mode.
  const legacySplit = splits.length > 0 && !lineAssigned;

  // Line items carrying no PTAE. An admin who ADDS a line to an already-funded
  // claim gets exactly this: the new line belongs to no bucket, so the funding
  // no longer covers the claim and approval is refused. Naming the count here is
  // the difference between a clear next step and a rejected approval later.
  const unassignedLines = lineAssigned
    ? liveLines.filter(l => !l.removed && l.allocations.length === 0).length
      // A line added in the form, on a claim split across PTAEs, has no bucket
      // to join - said now rather than once the claim is saved.
      + (soleBucket ? 0 : unsavedNewLines.length)
    : 0;

  const allocated = lineAssigned
    ? unassignedLines === 0
    : (legacySplit ? splits.every(s => s.ptae) : !!ptae);

  return (
    <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 mb-4 sm:mb-6">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-800">
            PTA Allocation <span className="text-stanford-red" title="Required before approval">*</span>
          </h3>
          <p className="text-sm text-gray-600">
            Assign funding before approving. This carries through to the monthly report.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {saveState === 'saved' && (
            <span className="text-xs text-green-700">Saved</span>
          )}
          <span className={`px-3 py-1 rounded-full text-xs font-medium ${
            allocated ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'
          }`}>
            {allocated
              ? (lineAssigned ? `Funded by line (${splits.length})` : 'Allocated')
              : 'Not allocated'}
          </span>
        </div>
      </div>

      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-2 rounded-lg text-sm">{error}</div>
      )}

      {/* A line assignment whose line no longer exists. The server drops these
          as soon as an admin saves the claim, so this only shows when the rows
          were replaced by some other route. */}
      {staleAssignments > 0 && !readOnly && (
        <div className="mb-4 bg-amber-50 border border-amber-200 text-amber-900 px-4 py-2 rounded-lg text-sm">
          <p className="font-medium">Some line items changed, so part of the PTAE assignment no longer applies.</p>
          <p className="mt-0.5 text-amber-800">
            {staleAssignments} assignment{staleAssignments === 1 ? '' : 's'} referred to line items that
            were removed. Use Assign PTAE to re-fund the affected lines.
          </p>
        </div>
      )}

      {/* The honest version of the old "cannot drift" banner. Buckets do follow
          the lines, but only for lines they already hold - a line ADDED after
          the assignment belongs to nothing, and the claim is then under-funded. */}
      {unassignedLines > 0 && !readOnly && (
        <div className="mb-4 bg-amber-50 border border-amber-200 text-amber-900 px-4 py-2 rounded-lg text-sm">
          <p className="font-medium">
            {unassignedLines} line item{unassignedLines === 1 ? ' is' : 's are'} not assigned to a PTAE.
          </p>
          <p className="mt-0.5 text-amber-800">
            The funding no longer covers the whole claim, so approval is blocked.
            Open Assign PTAE and put {unassignedLines === 1 ? 'it' : 'them'} in a bucket.
          </p>
        </div>
      )}

      {lineAssigned && !readOnly && staleAssignments === 0 && unassignedLines === 0 && (
        <div className="mb-4 bg-emerald-50 border border-emerald-200 text-emerald-900 px-4 py-2 rounded-lg text-sm">
          Funded line by line - each amount below is the total of the line items
          assigned to that PTAE, and it follows them when they change.
        </div>
      )}

      {/* Allocated by hand-typed amounts, before buckets existed. Left exactly as
          it is - rewriting someone's funding decision is not ours to do - but it
          cannot be edited here any more. */}
      {legacySplit && (
        <div className="mb-4">
          <div className="bg-gray-50 border border-gray-200 text-gray-700 px-4 py-2 rounded-lg text-sm mb-3">
            <p className="font-medium">Funded by amounts typed against each code.</p>
            <p className="mt-0.5 text-gray-600">
              This is how the claim was allocated before funding moved to line items.
              It still counts, and approval still checks it against the claim total.
              To change it, use Assign PTAE.
            </p>
          </div>
          <div className="rounded-lg border border-gray-200 overflow-hidden">
            <table className="w-full text-sm responsive-table ptae-alloc-table">
              <thead className="bg-gray-50 text-gray-500">
                <tr>
                  <th className="text-left px-3 py-2 font-medium">PTAE</th>
                  <th className="text-right px-3 py-2 font-medium">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {splits.map((s, i) => (
                  <tr key={s.id ?? s._id ?? i}>
                    <td data-cell="title" className="px-3 py-2 text-xs text-gray-800">
                      {s.ptae ? <PtaeCodeLabel code={s.ptae} index={ptaeIndex} /> : '-'}
                    </td>
                    <td data-label="Amount" className="px-3 py-2 text-right tabular-nums whitespace-nowrap">
                      {(parseFloat(s.amount) || 0).toFixed(2)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-gray-500 mt-1">
            Allocated {splits.reduce((a, s) => a + (parseFloat(s.amount) || 0), 0).toFixed(2)} of {total.toFixed(2)}
          </p>
        </div>
      )}

      {/* Not funded per line yet. There is no picker here any more: funding
          is assigned through Assign PTAE. A claim given one code for the whole
          claim before buckets is shown read-only. */}
      {!lineAssigned && !legacySplit && (
        ptae ? (
          <div>
            <p className="text-sm font-medium text-gray-700 mb-1">One PTAE for the whole claim</p>
            <div className="text-sm text-gray-800"><PtaeCodeLabel code={ptae} index={ptaeIndex} /></div>
            {!readOnly && (
              <p className="text-xs text-gray-500 mt-1">
                To change it, or to fund different days or receipts from different awards, use Assign PTAE.
              </p>
            )}
          </div>
        ) : (
          <p className="text-sm text-gray-700">
            {readOnly
              ? 'No PTAE has been assigned yet.'
              : lines.length > 0
                ? 'No PTAE assigned yet. Use Assign PTAE to put the line items in PTAE buckets - one bucket with every line funds the whole claim.'
                : 'This claim has no line items to assign a PTAE to.'}
          </p>
        )
      )}

      {/* What each PTAE carries - the split by bucket, on the claim page. It
          used to take opening the assigner to see these totals at all. Worked
          from the form's live values, so it moves as lines are edited. */}
      {lineAssigned && bucketRows.length > 0 && (
        <div className="mt-2">
          <div className="rounded-lg border border-gray-200 overflow-hidden">
            <table className="w-full text-sm responsive-table ptae-alloc-table">
              <thead className="bg-gray-50 text-gray-500">
                <tr>
                  <th className="text-left px-3 py-2 font-medium">PTAE</th>
                  <th className="text-right px-3 py-2 font-medium">Line items</th>
                  <th className="text-right px-3 py-2 font-medium">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {bucketRows.map(b => (
                  <tr key={b.id}>
                    <td data-cell="title" className="px-3 py-2 text-xs text-gray-800">
                      <PtaeCodeLabel code={b.ptae} index={ptaeIndex} />
                    </td>
                    <td data-label="Line items" className="px-3 py-2 text-right text-gray-700 whitespace-nowrap">
                      {/* One wrapper so the count and its note stay together
                          when the cell becomes a label/value row on phones. */}
                      <span>
                        {b.count}
                        {b.splitCount > 0 && (
                          <span className="ml-1 text-xs text-indigo-700">({b.splitCount} split)</span>
                        )}
                      </span>
                    </td>
                    <td data-label="Amount" className="px-3 py-2 text-right tabular-nums whitespace-nowrap">
                      <span className="block">
                        <span className="font-semibold text-gray-900">{money(b.amount)}</span>
                        {Math.abs(b.amount - b.saved) > 0.004 && (
                          <span className="block text-xs text-gray-500">saved {money(b.saved)}</span>
                        )}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-gray-50">
                <tr>
                  <td className="px-3 py-2 font-semibold text-gray-800" colSpan={2}>Allocated</td>
                  <td className={`px-3 py-2 text-right tabular-nums font-bold whitespace-nowrap ${
                    Math.abs(bucketsAllocated - total) <= 0.01 ? 'text-emerald-700' : 'text-amber-700'
                  }`}>
                    {money(bucketsAllocated)} of {money(total)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
          {linesEdited && !readOnly && (
            <p className="text-xs text-gray-600 mt-1">
              Line items have changed since the claim was last saved - the figures
              above already reflect them{unsavedNewLines.length > 0
                ? (soleBucket
                  ? `, including ${unsavedNewLines.length} new line${unsavedNewLines.length === 1 ? '' : 's'} `
                    + `(${money(unsavedNewValue)}), which join${unsavedNewLines.length === 1 ? 's' : ''} `
                    + `${soleBucket.ptae} when the claim is saved`
                  : `, apart from ${unsavedNewLines.length} new line${unsavedNewLines.length === 1 ? '' : 's'} `
                    + `(${money(unsavedNewValue)}) that will need a PTAE once the claim is saved`)
                : ''}. Save the claim to store them.
            </p>
          )}
        </div>
      )}

      {/* What each day or receipt is funded from, on the claim page itself -
          this is what the admin came back to see after assigning. Collapsed by
          default so a 30-day trip does not push the rest of the page away. */}
      {lineAssigned && lines.length > 0 && (
        <details className="mt-4 pt-1 sm:pt-4 border-t border-gray-200">
          <summary className="py-3 sm:py-0 text-sm font-medium text-stanford-red cursor-pointer">
            Line items and their PTAE ({lines.length})
          </summary>
          <div className="mt-0 sm:mt-3 rounded-lg border border-gray-200 overflow-hidden">
            <table className="w-full text-sm responsive-table ptae-alloc-table">
              <thead className="bg-gray-50 text-gray-500">
                <tr>
                  <th className="text-left px-3 py-2 font-medium">Line item</th>
                  <th className="text-left px-3 py-2 font-medium">PTAE</th>
                  <th className="text-right px-3 py-2 font-medium">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {liveLines.map(line => {
                  const codeOf = (splitId) => splits.find(sp => String(sp.id ?? sp._id) === String(splitId))?.ptae;
                  return (
                    <tr key={line.lineId} className={line.removed ? 'opacity-50' : ''}>
                      <td data-cell="full" className="px-3 py-2 text-gray-800 font-medium md:font-normal">
                        {line.label}
                        {line.detail && (
                          <span className="block text-xs text-gray-500 font-normal">{line.detail}</span>
                        )}
                        {line.removed && (
                          <span className="block text-xs text-amber-700 font-normal">Removed - goes when the claim is saved</span>
                        )}
                      </td>
                      <td data-label="PTAE" className="px-3 py-2 font-mono text-xs text-gray-700 break-words md:break-all">
                        {/* One wrapper so a split line's parts stay stacked when
                            the cell becomes a label/value row on phones. */}
                        <span className="block min-w-0">
                          {line.liveParts.length === 0 && (
                            <span className="text-amber-700 font-sans">Not assigned</span>
                          )}
                          {line.liveParts.length === 1 && (
                            <PtaeCodeLabel code={codeOf(line.liveParts[0].splitId)} index={ptaeIndex} />
                          )}
                          {line.liveParts.length > 1 && line.liveParts.map(pt => (
                            <span key={pt.splitId} className="block">
                              <PtaeCodeLabel code={codeOf(pt.splitId)} index={ptaeIndex} />
                              <span className="font-sans text-gray-500"> · {money(pt.liveAmount)}</span>
                            </span>
                          ))}
                        </span>
                      </td>
                      <td data-label="Amount" className="px-3 py-2 text-right tabular-nums text-gray-800 whitespace-nowrap">
                        <span className="block">
                          {money(line.liveAmount)}
                          {line.changed && !line.removed && (
                            <span className="block text-xs text-gray-500">was {money(line.amount)}</span>
                          )}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </details>
      )}

      {showAssigner && (
        <PtaeBucketAssigner
          claimType={claimType}
          claimTotal={total}
          lines={assignableLines}
          // Reopen an existing line allocation for editing rather than starting
          // from nothing - changing one day should not mean redoing all twenty.
          // An old single code opens as one bucket holding every line.
          initialBuckets={!lineAssigned && !legacySplit && ptae
            ? [{ ptae, lineIds: assignableLines.map(l => l.lineId), parts: [] }]
            : splits
            .map(sp => {
              const id = String(sp.id ?? sp._id);
              const whole = [];
              const parts = [];
              for (const l of assignableLines) {
                const allocs = l.allocations || (l.splitId ? [{ splitId: l.splitId, amount: l.amount }] : []);
                const mine = allocs.find(a => String(a.splitId) === id);
                if (!mine) continue;
                if (allocs.length > 1) parts.push({ lineId: l.lineId, amount: mine.amount });
                else whole.push(l.lineId);
              }
              return { ptae: sp.ptae, lineIds: whole, parts };
            })
            .filter(b => b.lineIds.length > 0 || b.parts.length > 0)}
          onCancel={() => setShowAssigner(false)}
          onSaved={handleAssignerSaved}
          save={saveLineBuckets}
        />
      )}

      {!readOnly && (
        <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3 mt-4 pt-4 border-t border-gray-200">
          {/* The one way into per-line funding. Offered whenever the claim has
              line items - "which days does this award pay for" is the question
              admins actually have. With unsaved line changes it saves the
              claim first, and says so. */}
          {(lines.length > 0 || (onSaveClaim && unsavedNewLines.length > 0)) && (
            <button
              onClick={openAssigner}
              disabled={savingFirst}
              className="min-h-[44px] sm:min-h-0 px-4 py-2 bg-stanford-red text-white rounded-lg hover:bg-stanford-light-red transition-colors disabled:opacity-50"
              title={linesEdited && onSaveClaim
                ? 'Save the claim so the assigner shows the line items as they are now, then assign PTAEs'
                : 'Assign a PTAE to each day or receipt, in groups'}
            >
              {savingFirst
                ? 'Saving…'
                : (linesEdited && onSaveClaim ? 'Save changes & assign PTAE' : 'Assign PTAE')}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

ClaimPtaeAllocation.propTypes = {
  claimType: PropTypes.string.isRequired,
  claimId: PropTypes.string.isRequired,
  claimTotal: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  readOnly: PropTypes.bool,
  onChange: PropTypes.func,
  // Kept for the forms that still pass it, but never called now: it flagged
  // the gap before the single-PTAE picker's debounced save, and the picker is
  // gone. The assigner saves as it closes, so there is no gap to guard.
  onDirtyChange: PropTypes.func,
  // Called after each load with { linePtae, lineParts, splits, ptae, lineAssigned }
  // so the page can put the PTAE into its Excel export.
  onAllocationLoaded: PropTypes.func,
  // { [lineId]: amount } as the form currently has it, so the lines and buckets
  // follow an edit before it is saved.
  liveLineValues: PropTypes.object,
  // Changes (truthy) after the claim is saved, to reload the allocation.
  refreshKey: PropTypes.any,
  // Saves the claim; resolves true once saved. Called before the assigner
  // opens when lines were added, removed or changed since the last save.
  onSaveClaim: PropTypes.func,
};

export default ClaimPtaeAllocation;
